'use client'

import useLoggedUser from '@/features/auth/hooks/useLoggedUser'
import { createConfigForHSCConfig } from '@/features/caching/config/tanstack'
import useCurrentMarket from '@/features/caching/hooks/useCurrentMarket'
import { mockedFlyerTemplate } from '@/features/flyer/mocks/flyer-template'
import { useFlyerBuilderState } from '@/features/flyer/store/FlyerBuilderStateProvider'
import { currencySymbolMap } from '@/features/markets/configs/currencies'
import { TranslationBundles } from '@/features/sitecore/configs/translation-bundles'
import { useSitecoreState } from '@/features/sitecore/store/useSitecoreState'
import { _isFeatureEnabled } from '@/utils/environment/feature-flags'
import { useQuery } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import React, { useEffect, useRef, useState } from 'react'
import { FaMinus, FaPlus } from 'react-icons/fa'
import { Button, FlyerBuildetPagination } from '../uiComponents'

const secondPageTemplate = `<main id="second-page" style="display: flex; flex-direction: column; width: 100%"></main>`
const emptyFlyerTemplate = `<main style="display: flex; flex-direction: column; width: 100%"></main>`

const FlyerBuilderPreview = () => {
	const previewRef = useRef<HTMLDivElement>(null)
	const firstSheetRef = useRef<HTMLDivElement>(null)
	const secondSheetRef = useRef<HTMLDivElement>(null)
	const thirdSheetRef = useRef<HTMLDivElement>(null)
	const [activeSheet, setActiveSheet] = useState<HTMLDivElement | null>(null)
	const [sheetsWithSections, setSheetsWithSections] = useState<number>(0)
	const [currentScaleValueNumber, setCurrentScaleValueNumber] = useState<number>(1)
	const hscConfigQuery = useQuery(createConfigForHSCConfig())
	const sitecoreStore = useSitecoreState((state) => state)
	const market = useCurrentMarket()
	const user = useLoggedUser()
	const flyerState = useFlyerBuilderState((state) => state)

	//TODO DELETE NEW-BUILD-YOUR-FLYER-ACTIVATION (remove all byfActivation occurrences)
	const byfActivationKey = sitecoreStore.getLabel(TranslationBundles.FEATURES_ACTIVATION, 'MSC-NEW-BUILD-YOUR-FLYER-ACTIVATION', '')
	const isNewBYFEnabled = _isFeatureEnabled(byfActivationKey, market, user)

	const bundleTemplate = flyerState.flyerType === 'tactical' ? TranslationBundles.BUILD_YOUR_FLYER_TACTICAL : TranslationBundles.BUILD_YOUR_FLYER_PRODUCT
	const key = `MSC-BUILD_YOUR_FLYER-${flyerState.flyerType.toUpperCase()}-FLYER-TEMPLATE-${flyerState.orientation.toUpperCase()}-${flyerState.template}`
	const flyerTemplate = isNewBYFEnabled ? sitecoreStore.getLabel(bundleTemplate, key, mockedFlyerTemplate) : sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-TACTICAL-FLYER-TEMPLATE', mockedFlyerTemplate)

	const sanitizedHTMLTemplate = DOMPurify.sanitize(flyerTemplate)
	const sheetsContentRef = useRef<{ firstSheet: string; secondSheet: string; thirdSheet: string }>({
		firstSheet: sanitizedHTMLTemplate,
		secondSheet: secondPageTemplate,
		thirdSheet: emptyFlyerTemplate,
	})

	const getSheets = () => ({
		firstSheet: firstSheetRef.current,
		secondSheet: secondSheetRef.current,
		thirdSheet: thirdSheetRef.current,
	})

	const applyScale = (scale: number) => {
		const { firstSheet, secondSheet, thirdSheet } = getSheets()
		if (!firstSheet || !secondSheet || !thirdSheet) return
		// prevent overzooming
		if (scale < 0.4 || scale > 1.4) return
		firstSheet.style.scale = scale.toString()
		secondSheet.style.scale = scale.toString()
		thirdSheet.style.scale = scale.toString()
		if (activeSheet?.getAttribute('id') !== 'first-flyer-page') firstSheet.style.transform = 'translateY(150%)'
		if (activeSheet?.getAttribute('id') !== 'second-flyer-page') secondSheet.style.transform = 'translateY(150%)'
		if (activeSheet?.getAttribute('id') !== 'third-flyer-page') thirdSheet.style.transform = 'translateY(150%)'
		if (activeSheet) activeSheet.style.transform = 'translateY(0%)'
	}

	const calculateScale = () => {
		const preview = previewRef.current
		const { firstSheet, secondSheet, thirdSheet } = getSheets()
		if (!preview || !firstSheet || !secondSheet || !thirdSheet) return

		const sheetWidth = firstSheet.offsetWidth
		const sheetHeight = firstSheet.offsetHeight
		const previewWidth = preview.offsetWidth
		const previewHeight = preview.offsetHeight - 96

		const scale = Math.min(previewWidth / sheetWidth, previewHeight / sheetHeight)
		applyScale(scale)
	}

	const handleFlyerPages = () => {
		const { firstSheet, secondSheet, thirdSheet } = getSheets()
		if (!firstSheet || !secondSheet || !thirdSheet) return

		const sheetBodyHeight = firstSheet.offsetHeight - 40
		let firstPageSectionsHeight = 0
		let secondPageSectionsHeight = 0
		const firstSheetSections = firstSheet.querySelectorAll('section')
		firstSheetSections.forEach((section) => {
			if (section.style.display === 'none') return
			const sectionHeight = section.offsetHeight + parseFloat(window.getComputedStyle(section).marginTop)
			if (firstPageSectionsHeight + sectionHeight > sheetBodyHeight) {
				secondSheet.querySelector('main')?.appendChild(section)
				secondPageSectionsHeight += sectionHeight
				return
			}
			firstPageSectionsHeight += sectionHeight
		})
		const secondSheetSections = secondSheet.querySelectorAll('section')

		secondSheetSections.forEach((section) => {
			if (section.style.display === 'none') return
			const sectionHeight = section.offsetHeight + parseFloat(window.getComputedStyle(section).marginTop)
			/*	if (firstPageSectionsHeight + sectionHeight <= sheetBodyHeight && sheetsWithSections === 2) {
				firstSheet.querySelector('main')?.appendChild(section)
				firstPageSectionsHeight += sectionHeight
				return

			if (secondPageSectionsHeight + sectionHeight > sheetBodyHeight) {
				thirdSheet.querySelector('main')?.appendChild(section)
				return
			}}*/
			secondPageSectionsHeight += sectionHeight
		})
		const thirdSheetSections = thirdSheet.querySelectorAll('section')
		thirdSheetSections.forEach((section) => {
			if (section.style.display === 'none') return
			const sectionHeight = section.offsetHeight + parseFloat(window.getComputedStyle(section).marginTop)
			if (secondPageSectionsHeight + sectionHeight <= sheetBodyHeight && sheetsWithSections === 3) {
				secondSheet.querySelector('main')?.appendChild(section)
				secondPageSectionsHeight += sectionHeight
				return
			}
		})

		const sheetsCount = [firstSheet, secondSheet, thirdSheet].filter((sheet) => {
			const sections = sheet.querySelectorAll('section')
			return sections.length > 0 && Array.from(sections).every((section) => section.style.display !== 'none')
		}).length

		if (sheetsCount !== sheetsWithSections) {
			setSheetsWithSections(sheetsCount)
		}

		sheetsContentRef.current = {
			firstSheet: firstSheet.innerHTML,
			secondSheet: secondSheet.innerHTML,
			thirdSheet: thirdSheet.innerHTML,
		}
	}

	const handleFlyerDrag = (event: React.MouseEvent) => {
		if (!activeSheet) return

		const startY = event.clientY
		const initialTransform = activeSheet.style.transform
		const initialTranslateY = initialTransform.includes('translateY') ? parseFloat(initialTransform.split('translateY(')[1].split('%')[0]) : 0

		const onMouseMove = (moveEvent: MouseEvent) => {
			const deltaY = moveEvent.clientY - startY
			const sheetScale = activeSheet.style.scale
			const sheetHeight = activeSheet.offsetHeight * parseFloat(sheetScale)
			const sheetParentHeight = activeSheet.parentNode ? (activeSheet.parentNode as HTMLElement).offsetHeight : 0

			if (sheetParentHeight && sheetHeight > sheetParentHeight) {
				const sheetOverflowHeight = (sheetHeight - sheetParentHeight + 80) / 2
				let newTranslateY = initialTranslateY + (deltaY / activeSheet.offsetHeight) * 100
				const maxTranslate = (sheetOverflowHeight / sheetHeight) * 100
				const minTranslate = -((sheetOverflowHeight / sheetHeight) * 100)
				newTranslateY = Math.max(minTranslate, Math.min(maxTranslate, newTranslateY))
				activeSheet.style.transform = `translateY(${newTranslateY}%)`
			}
		}

		const onMouseUp = () => {
			window.removeEventListener('mousemove', onMouseMove)
			window.removeEventListener('mouseup', onMouseUp)
		}

		window.addEventListener('mousemove', onMouseMove)
		window.addEventListener('mouseup', onMouseUp)
	}

	const handleFlyerZoom = (handler: string) => {
		if (!activeSheet) return

		const currentScale = activeSheet.style.scale
		const currentScaleValue = currentScale ? parseFloat(currentScale) : 1
		const newScaleValue = handler === 'plus' ? currentScaleValue + 0.1 : currentScaleValue - 0.1

		applyScale(newScaleValue)
		setCurrentScaleValueNumber(newScaleValue)
		handleFlyerPages()
	}

	const changeActiveSheet = (direction: string) => {
		const { firstSheet, secondSheet, thirdSheet } = getSheets()
		if (!firstSheet || !secondSheet || !thirdSheet) return

		if (direction === 'next') {
			if (activeSheet?.getAttribute('id') === 'first-flyer-page') {
				setActiveSheet(secondSheet)
				return
			}
			if (activeSheet?.getAttribute('id') === 'second-flyer-page') {
				setActiveSheet(thirdSheet)
				return
			}
		}
		if (direction === 'prev') {
			if (activeSheet?.getAttribute('id') === 'second-flyer-page') {
				setActiveSheet(firstSheet)
				return
			}
			if (activeSheet?.getAttribute('id') === 'third-flyer-page') {
				setActiveSheet(secondSheet)
				return
			}
		}
	}

	useEffect(() => {
		const { firstSheet, secondSheet, thirdSheet } = getSheets()
		if (!firstSheet || !secondSheet || !thirdSheet) return
		setActiveSheet(firstSheet)
		calculateScale()
		handleFlyerPages()
		window.addEventListener('resize', calculateScale)
		return () => {
			window.removeEventListener('resize', calculateScale)
			if (activeSheet) {
				activeSheet.style.transform = 'translateY(0)'
				activeSheet.style.transition = 'all .250s ease-in-out'
				setTimeout(() => {
					activeSheet.style.transition = ''
				}, 300)
			}
			secondSheet.style.transform = 'translateY(150%)'
			thirdSheet.style.transform = 'translateY(150%)'
		}
	}, [])

	useEffect(() => {
		if (activeSheet) {
			activeSheet.style.transform = 'translateY(0)'
			activeSheet.style.transition = 'all .250s ease-in-out'
			const { firstSheet, secondSheet, thirdSheet } = getSheets()
			if (!firstSheet || !secondSheet || !thirdSheet) return
			if (activeSheet?.getAttribute('id') === 'first-flyer-page') {
				secondSheet.style.transform = 'translateY(150%)'
				secondSheet.style.transition = 'all .250s ease-in-out'
				thirdSheet.style.transform = 'translateY(150%)'
				thirdSheet.style.transition = 'all .250s ease-in-out'
			}
			if (activeSheet?.getAttribute('id') === 'second-flyer-page') {
				firstSheet.style.transform = 'translateY(-150%)'
				firstSheet.style.transition = 'all .250s ease-in-out'
				thirdSheet.style.transform = 'translateY(150%)'
				thirdSheet.style.transition = 'all .250s ease-in-out'
			}
			if (activeSheet?.getAttribute('id') === 'third-flyer-page') {
				firstSheet.style.transform = 'translateY(-150%)'
				firstSheet.style.transition = 'all .250s ease-in-out'
				secondSheet.style.transform = 'translateY(-150%)'
				secondSheet.style.transition = 'all .250s ease-in-out'
			}
			setTimeout(() => {
				firstSheet.style.transition = ''
				secondSheet.style.transition = ''
				thirdSheet.style.transition = ''
			}, 300)
		}
	}, [activeSheet])

	useEffect(() => {
		if (activeSheet ? activeSheet.getAttribute('data-value')! >= sheetsWithSections.toString() : false) {
			changeActiveSheet('prev')
			return
		} else {
			changeActiveSheet('next')
		}
	}, [sheetsWithSections])

	// change active sheet(preview), when clicking on the pricelist (offer) in step 3, tactical flyer
	const previousPriceListNamesRef = useRef<string[]>([])
	useEffect(() => {
		const { firstSheet, secondSheet } = getSheets()
		const currentPriceListNames = flyerState.steps.priceList.priceLists.map((priceList) => priceList?.priceList?.name || '')
		const hasChangedIndex = currentPriceListNames.findIndex((name, index) => name !== previousPriceListNamesRef.current[index])
		if (hasChangedIndex === 0) {
			setActiveSheet(firstSheet)
		} else if (hasChangedIndex > 0) {
			setActiveSheet(secondSheet)
		}
		previousPriceListNamesRef.current = currentPriceListNames
	}, [flyerState.steps.priceList.priceLists])

	// show only total price in the preview if HSC is NOT enabled
	useEffect(() => {
		if (hscConfigQuery.isLoading) return
		const hscConfig = hscConfigQuery.data
		if (!hscConfig || !hscConfig.enabled || hscConfig.summedToBasePrice) {
			const hscPrice = document.getElementById('hsc-price-section')
			const hscPriceMultipleItins = document.getElementsByClassName('hsc-div-multiple-itins')
			if (hscPrice) {
				hscPrice.style.display = 'none'
			}
			if (hscPriceMultipleItins) {
				Array.from(hscPriceMultipleItins).forEach((element) => {
					const htmlElement = element as HTMLElement
					htmlElement.style.display = 'none'
				})
			}
		}
	}, [hscConfigQuery])

	// Currency symbol
	useEffect(() => {
		const currencySymbol = currencySymbolMap[market.currency]
		const currencyElements = document.querySelectorAll('.currency')
		currencyElements.forEach((element) => (element.innerHTML = currencySymbol))
	}, [hscConfigQuery])

	useEffect(() => {
		handleFlyerPages()
	}, [flyerState.steps])

	const onSelectPreview = (selectedSheet: string) => {
		const sheets = getSheets()
		const activeSheet = Object.entries(sheets)
			.filter(
				([
					key,
				]) => selectedSheet === key.toString()
			)
			.map(([, value]) => value)[0]
		setActiveSheet(activeSheet)
	}

	return (
		<div ref={previewRef} id="flyer-wrapper" className="relative flex items-center justify-center flex-1 bg-grey border border-darker-grey overflow-hidden" onMouseDown={handleFlyerDrag}>
			<div className="absolute top-8 right-6 flex flex-col gap-4 z-20">
				<Button variant="zoomButton" onClick={() => handleFlyerZoom('plus')} className={`${Number(currentScaleValueNumber) > 1.3 ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
					<FaPlus color="#000033" size={12} />
				</Button>
				<Button variant="zoomButton" onClick={() => handleFlyerZoom('minus')} className={`${Number(currentScaleValueNumber) < 0.4 ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
					<FaMinus color="#000033" size={12} />
				</Button>
			</div>

			<div ref={firstSheetRef} data-value="1" id="first-flyer-page" className={`absolute ${flyerState.orientation === 'portrait' ? 'w-printing-sheet h-printing-sheet' : 'w-printing-landscape-sheet h-printing-landscape-sheet'} bg-white p-5 transition-scale`} dangerouslySetInnerHTML={{ __html: sheetsContentRef.current.firstSheet }}></div>
			<div ref={secondSheetRef} data-value="2" id="second-flyer-page" className={`absolute ${flyerState.orientation === 'portrait' ? 'w-printing-sheet h-printing-sheet' : 'w-printing-landscape-sheet h-printing-landscape-sheet'} bg-white p-5 transition-scale`} dangerouslySetInnerHTML={{ __html: sheetsContentRef.current.secondSheet }}></div>
			<div ref={thirdSheetRef} data-value="3" id="third-flyer-page" className={`absolute ${flyerState.orientation === 'portrait' ? 'w-printing-sheet h-printing-sheet' : 'w-printing-landscape-sheet h-printing-landscape-sheet'} bg-white p-5  transition-scale`} dangerouslySetInnerHTML={{ __html: sheetsContentRef.current.thirdSheet }}></div>

			{isNewBYFEnabled && flyerState.flyerType === 'tactical' && (
				<div className={`absolute bottom-8 left-6  gap-4 z-20 flex flex-col transition-scale origin-bottom-left scale-[0.15] `}>
					<div className={`  ${flyerState.orientation === 'portrait' ? 'w-printing-sheet h-printing-sheet' : 'w-printing-landscape-sheet h-printing-landscape-sheet'}  bg-white p-2 cursor-pointer ${activeSheet?.getAttribute('id') === 'first-flyer-page' ? 'border-6 border-black' : ''}`} dangerouslySetInnerHTML={{ __html: sheetsContentRef.current.firstSheet }} onClick={() => onSelectPreview('firstSheet')}></div>
					{sheetsWithSections >= 2 && <div className={`  ${flyerState.orientation === 'portrait' ? 'w-printing-sheet h-printing-sheet' : 'w-printing-landscape-sheet h-printing-landscape-sheet'} bg-white p-2 cursor-pointer ${activeSheet?.getAttribute('id') === 'second-flyer-page' ? 'border-6 border-black' : ''}`} dangerouslySetInnerHTML={{ __html: sheetsContentRef.current.secondSheet }} onClick={() => onSelectPreview('secondSheet')}></div>}
					{sheetsWithSections >= 3 && <div className={` ${flyerState.orientation === 'portrait' ? 'w-printing-sheet h-printing-sheet' : 'w-printing-landscape-sheet h-printing-landscape-sheet'} bg-white p-2 cursor-pointer ${activeSheet?.getAttribute('id') === 'third-flyer-page' ? 'border-6 border-black' : ''}`} dangerouslySetInnerHTML={{ __html: sheetsContentRef.current.thirdSheet }} onClick={() => onSelectPreview('thirdSheet')}></div>}
				</div>
			)}

			{isNewBYFEnabled && activeSheet !== null && (
				<div className="absolute bottom-8 right-6 flex flex-col items-center gap-4">
					<div className="flex flex-col gap-2">
						<FlyerBuildetPagination isActive={activeSheet?.getAttribute('data-value') === '1'} onClick={() => setActiveSheet(firstSheetRef.current)}>
							1
						</FlyerBuildetPagination>
						{sheetsWithSections >= 2 && (
							<FlyerBuildetPagination isActive={activeSheet?.getAttribute('data-value') === '2'} onClick={() => setActiveSheet(secondSheetRef.current)}>
								2
							</FlyerBuildetPagination>
						)}
						{sheetsWithSections >= 3 && (
							<FlyerBuildetPagination isActive={activeSheet?.getAttribute('data-value') === '3'} onClick={() => setActiveSheet(thirdSheetRef.current)}>
								3
							</FlyerBuildetPagination>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

export { FlyerBuilderPreview }
